import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Zap, ArrowLeft, Plus, Trash2, X, Pencil, Target, Users2, PartyPopper } from "lucide-react";
import { ActionDialog } from "@/components/ActionDialog";
import { AvatarView } from "@/components/AvatarView";
import { PointsBubble } from "@/components/PointsBubble";
import { ClassTools } from "@/components/ClassTools";
import { useI18n } from "@/lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const COLORS = ["#00E5FF", "#FF007F", "#00C853", "#AA00FF", "#FF6D00", "#FFD600", "#2962FF", "#FF3333"];
const inGroup = (s, gid) => s.group_id === gid || (s.group_ids || []).includes(gid);
const todayMD = new Date().toISOString().slice(5, 10);
const isBday = (s) => s.birthday_active || (s.birthday || "").slice(5, 10) === todayMD;

function ProgressCard({ b, cur, groupName, t }) {
  const pct = Math.min(100, Math.round((cur / Number(b.target)) * 100));
  const done = pct >= 100;
  const color = b.color || "#00E5FF";
  return (
    <motion.div data-testid={`progress-bar-${b.id}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-3xl border-2 p-5 bg-card relative overflow-hidden"
      style={{ borderColor: done ? color : color + "55", boxShadow: done ? `0 0 28px ${color}66` : "none" }}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <span className="font-display font-extrabold text-lg flex items-center gap-2">
          <Target className="w-5 h-5" style={{ color }} /> {b.label || t("objective")}
          {groupName && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{groupName}</span>}
        </span>
        <span className="font-mono font-extrabold text-xl" style={{ color }}>{cur} / {b.target}</span>
      </div>
      <div className="h-6 rounded-full bg-muted overflow-hidden">
        <motion.div className="h-full rounded-full" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ type: "spring", stiffness: 60 }} style={{ background: `linear-gradient(90deg,${color},${color}99)` }} />
      </div>
      {done ? (
        <motion.div data-testid={`bar-done-${b.id}`} initial={{ scale: 0.8 }} animate={{ scale: [1, 1.06, 1] }} transition={{ repeat: Infinity, duration: 1.6 }}
          className="mt-3 font-display font-extrabold text-lg flex items-center gap-2" style={{ color }}>
          <PartyPopper className="w-5 h-5" /> {t("objectiveCompleted")}{b.reward ? ` · ${b.reward}` : ""}
        </motion.div>
      ) : b.reward ? <div className="text-sm text-muted-foreground mt-2">{b.reward}</div> : null}
    </motion.div>
  );
}

export default function AulaTab({ classId, cls, students, groups, events, rewards, badges, medals, attToday, reload }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [dialog, setDialog] = useState({ open: false, target: null, student: null });
  const [addOpen, setAddOpen] = useState(false);
  const [names, setNames] = useState("");
  const [activeGroup, setActiveGroup] = useState(null);
  const [newG, setNewG] = useState({ open: false, name: "", color: COLORS[0] });
  const [editG, setEditG] = useState(null);
  const [dragId, setDragId] = useState(null);

  const active = students.filter((s) => s.active_status !== "unsubscribed");
  const members = (gid) => active.filter((s) => inGroup(s, gid));
  const nonMembers = (gid) => active.filter((s) => !inGroup(s, gid));

  const group = activeGroup ? groups.find((g) => g.id === activeGroup) : null;
  const ctxStudents = group ? members(group.id) : active;
  const targetType = group ? "group" : "class";
  const targetId = group ? group.id : null;
  const ctxLabel = group ? group.name : t("classroom");
  const totalPoints = ctxStudents.reduce((a, s) => a + s.points, 0);

  const bars = (cls?.progress_bars || []).filter((b) => b.visible && Number(b.target) > 0 &&
    (group ? b.scope === "group" && b.group_id === group.id : b.scope !== "group"));
  const barCurrent = (b) => {
    const sum = b.scope === "group" && b.group_id ? members(b.group_id).reduce((a, s) => a + s.points, 0) : active.reduce((a, s) => a + s.points, 0);
    return Math.max(0, sum - (b.baseline || 0));
  };

  const openAction = (target, student = null) => setDialog({ open: true, target, student });
  const addStudents = async () => {
    const l = names.split("\n").map((n) => n.trim()).filter(Boolean);
    if (!l.length) return toast.error("Pega al menos un nombre");
    await api.bulkStudents({ class_id: classId, names: l, group_id: targetId });
    toast.success(`${l.length} alumno(s) añadidos 🎉`); setNames(""); setAddOpen(false); reload();
  };
  const createGroup = async () => {
    if (!newG.name.trim()) return toast.error("Escribe un nombre");
    await api.createGroup({ class_id: classId, name: newG.name, color: newG.color });
    setNewG({ open: false, name: "", color: COLORS[0] }); toast.success("Grupo creado"); reload();
  };
  const dropOn = async (gid) => { if (!dragId) return; await api.addStudentToGroup(gid, dragId); setDragId(null); toast.success("Alumno movido al grupo"); reload(); };

  return (
    <div className="space-y-6 pb-28">
      {/* CONTEXT BAR */}
      <div className="flex flex-wrap items-center gap-3 rounded-3xl border-2 p-3 bg-card" style={{ borderColor: (group?.color || cls.color) + "66" }}>
        {group && <button data-testid="back-to-aula" onClick={() => setActiveGroup(null)} className="h-12 px-4 rounded-2xl bg-muted font-bold flex items-center gap-2 hover:bg-accent"><ArrowLeft className="w-5 h-5" /> {t("classroom")}</button>}
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shrink-0" style={{ background: (group?.color || cls.color) + "22" }}>{group ? "❖" : cls.emoji || "🎓"}</span>
          <div className="min-w-0">
            <div data-testid="context-label" className="font-display font-extrabold text-xl truncate">{group ? group.name : cls.name}</div>
            <div className="text-xs text-muted-foreground">{ctxStudents.length} {t("studentsLower")} · {group ? t("group") : t("wholeClassTarget")}</div>
          </div>
        </div>
        <span data-testid="aula-total" className="px-4 h-12 rounded-2xl bg-primary/15 text-primary font-extrabold flex items-center gap-2 text-lg"><Zap className="w-5 h-5" /> {totalPoints}</span>
        <Button data-testid="context-points-btn" onClick={() => openAction({ type: targetType, id: targetId, label: group ? group.name : t("wholeClassTarget") })}
          className="h-12 px-6 rounded-2xl text-base font-extrabold gap-2 glow-primary ml-auto"><Users2 className="w-5 h-5" /> {t("pointsTo")} {ctxLabel}</Button>
        <Button data-testid="add-students-btn" variant="outline" onClick={() => setAddOpen(true)} className="h-12 px-5 rounded-2xl font-bold gap-2"><UserPlus className="w-5 h-5" /> {t("add")}</Button>
      </div>

      {/* PROGRESS */}
      {bars.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="class-progress-bars">
          {bars.map((b) => <ProgressCard key={b.id} b={b} cur={barCurrent(b)} t={t} groupName={b.scope === "group" && !group ? (groups.find((g) => g.id === b.group_id)?.name || t("group")) : null} />)}
        </div>
      )}

      {/* STUDENTS */}
      {ctxStudents.length === 0 ? (
        <div className="bg-card rounded-3xl border-2 border-dashed border-border p-12 text-center text-muted-foreground">{t("noStudentsHere")}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
          {ctxStudents.map((s, i) => {
            const absent = attToday[s.id] === "absent";
            return (
              <motion.div key={s.id} data-testid={`student-card-${s.id}`} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.02 }}
                draggable onDragStart={() => setDragId(s.id)} onDragEnd={() => setDragId(null)}
                onClick={() => openAction({ type: "student", id: s.id, label: s.name }, s)}
                className={`group bg-card rounded-3xl border-2 border-border p-4 flex flex-col items-center hover:-translate-y-1.5 hover:glow-primary transition-transform relative cursor-pointer ${absent ? "opacity-60" : ""}`}>
                {absent && <span className="absolute top-3 left-3 z-10 w-7 h-7 rounded-full flex items-center justify-center bg-red-500"><X className="w-4 h-4 text-white" /></span>}
                <button data-testid={`open-profile-${s.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/clase/${classId}/alumno/${s.id}`); }}
                  className="absolute top-2 right-2 z-10 text-xs font-bold px-2.5 py-1 rounded-full bg-muted sm:opacity-0 group-hover:opacity-100 transition-opacity hover:bg-accent">{t("profile")}</button>
                <div className="relative mt-3">
                  <AvatarView student={s} size={96} faded={absent} plain />
                  <span className="absolute bottom-0 right-0 translate-x-1 translate-y-1 z-40"><PointsBubble value={s.points} testid={`points-${s.id}`} /></span>
                </div>
                <div className="mt-3 font-extrabold text-base text-center leading-tight">{isBday(s) && <span data-testid={`bday-flag-${s.id}`}>🎂 </span>}{s.name}</div>
                <div className="flex items-center gap-1 mt-1.5 min-h-[22px]">
                  {(s.medals || []).slice(0, 5).map((m, idx) => <img key={idx} src={m.image} alt={m.name} title={m.name} className="w-6 h-6 rounded-full object-cover" />)}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* GROUPS AS MINI-CLASSROOMS */}
      {!group && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="font-display font-extrabold text-xl">{t("groupsOfClass")}</h3>
            <Button data-testid="open-new-group" onClick={() => setNewG({ open: true, name: "", color: COLORS[0] })} className="rounded-2xl h-11 px-5 font-bold gap-2"><Plus className="w-5 h-5" /> {t("createGroup")}</Button>
          </div>
          {groups.length === 0 ? (
            <div className="bg-card rounded-3xl border-2 border-dashed border-border p-8 text-center text-muted-foreground">{t("noGroupsYet")}</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {groups.map((g) => {
                const m = members(g.id);
                return (
                  <div key={g.id} data-testid={`group-col-${g.id}`} onClick={() => setActiveGroup(g.id)}
                    onDragOver={(e) => e.preventDefault()} onDrop={() => dropOn(g.id)}
                    className="bg-card rounded-3xl border-2 p-4 cursor-pointer hover:-translate-y-1 transition-transform relative"
                    style={{ borderColor: g.color + "77", background: g.color + "0D" }}>
                    <button data-testid={`edit-group-${g.id}`} onClick={(e) => { e.stopPropagation(); setEditG(g); }} className="absolute top-3 right-3 w-9 h-9 rounded-xl bg-card border border-border flex items-center justify-center hover:bg-accent"><Pencil className="w-4 h-4" /></button>
                    <div className="font-display font-extrabold text-lg truncate pr-10" style={{ color: g.color }}>{g.name}</div>
                    <div className="text-xs text-muted-foreground mb-3">{m.length} {t("studentsLower")} · {m.reduce((a, s) => a + s.points, 0)} pts</div>
                    <div className="flex flex-wrap gap-1.5 min-h-[44px]">
                      {m.slice(0, 8).map((s) => <AvatarView key={s.id} student={s} size={40} plain />)}
                      {m.length === 0 && <span className="text-xs text-muted-foreground">{t("dragHere")}</span>}
                      {m.length > 8 && <span className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center text-xs font-bold">+{m.length - 8}</span>}
                    </div>
                    <div className="mt-3 text-xs font-bold" style={{ color: g.color }}>{t("enterGroup")} →</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <ClassTools classId={classId} students={ctxStudents.filter((s) => attToday[s.id] !== "absent")} contextLabel={ctxLabel}
        targetType={targetType} targetId={targetId} screens={cls?.screens} reload={reload} />

      {/* Add students */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle className="font-display text-xl">{t("addStudentsTitle")} {group ? `· ${group.name}` : ""}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">{t("oneNamePerLine")}</p>
          <Textarea data-testid="bulk-names-input" value={names} onChange={(e) => setNames(e.target.value)} rows={8} placeholder={"Juan\nMaría\nCarlos"} className="rounded-xl font-mono" />
          <DialogFooter><Button data-testid="save-students-btn" onClick={addStudents} className="rounded-full h-12 px-6 font-bold">{t("add")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New group */}
      <Dialog open={newG.open} onOpenChange={(v) => setNewG((n) => ({ ...n, open: v }))}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle className="font-display text-xl">{t("newGroupTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <Input data-testid="group-name-input" value={newG.name} onChange={(e) => setNewG({ ...newG, name: e.target.value })} placeholder={t("groupNamePh")} className="rounded-xl h-12 text-base" onKeyDown={(e) => e.key === "Enter" && createGroup()} />
            <div className="flex gap-2 flex-wrap">{COLORS.map((c) => <button key={c} onClick={() => setNewG({ ...newG, color: c })} className="w-10 h-10 rounded-full transition-transform hover:scale-110" style={{ background: c, outline: newG.color === c ? "3px solid hsl(var(--foreground))" : "none", outlineOffset: 2 }} />)}</div>
          </div>
          <DialogFooter><Button data-testid="add-group-btn" onClick={createGroup} className="rounded-full h-12 px-6 font-bold">{t("createGroup")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit group */}
      <Dialog open={!!editG} onOpenChange={(v) => !v && setEditG(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle className="font-display text-xl">{t("editGroupTitle")}</DialogTitle></DialogHeader>
          {editG && (
            <div className="space-y-4 py-1">
              <Input data-testid="edit-group-name" defaultValue={editG.name} className="rounded-xl h-12 text-base"
                onBlur={async (e) => { const v = e.target.value.trim(); if (v && v !== editG.name) { await api.updateGroup(editG.id, { name: v }); reload(); } }} />
              <div className="flex gap-2 flex-wrap">{COLORS.map((c) => <button key={c} onClick={async () => { await api.updateGroup(editG.id, { color: c }); setEditG({ ...editG, color: c }); reload(); }} className="w-9 h-9 rounded-full" style={{ background: c, outline: editG.color === c ? "3px solid hsl(var(--foreground))" : "none", outlineOffset: 2 }} />)}</div>
              <div>
                <div className="text-sm font-bold mb-2">{t("inGroup")}</div>
                <div className="flex flex-wrap gap-2">
                  {members(editG.id).map((s) => (
                    <button key={s.id} data-testid={`remove-${editG.id}-${s.id}`} onClick={async () => { await api.removeStudentFromGroup(editG.id, s.id); reload(); }}
                      className="flex items-center gap-1.5 p-1.5 pr-2 rounded-xl bg-muted hover:bg-destructive hover:text-white text-sm font-semibold"><AvatarView student={s} size={28} plain /> {s.name} <X className="w-3.5 h-3.5" /></button>
                  ))}
                  {members(editG.id).length === 0 && <span className="text-xs text-muted-foreground">{t("noStudents")}</span>}
                </div>
              </div>
              <div>
                <div className="text-sm font-bold mb-2">{t("addToGroup")}</div>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-auto scrollbar-thin">
                  {nonMembers(editG.id).map((s) => (
                    <button key={s.id} data-testid={`add-student-${s.id}`} onClick={async () => { await api.addStudentToGroup(editG.id, s.id); reload(); }}
                      className="flex items-center gap-1.5 p-1.5 pr-2 rounded-xl hover:bg-accent text-sm font-semibold"><AvatarView student={s} size={28} plain /> {s.name} <Plus className="w-3.5 h-3.5 text-primary" /></button>
                  ))}
                  {nonMembers(editG.id).length === 0 && <span className="text-xs text-muted-foreground">{t("allInside")}</span>}
                </div>
              </div>
              <Button data-testid={`del-group-${editG.id}`} variant="outline" onClick={async () => { if (window.confirm("¿Eliminar grupo? Los alumnos no se borran.")) { await api.deleteGroup(editG.id); setEditG(null); setActiveGroup(null); reload(); } }}
                className="rounded-full w-full h-11 gap-2 text-destructive hover:bg-destructive hover:text-white"><Trash2 className="w-4 h-4" /> {t("delGroup")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ActionDialog open={dialog.open} onOpenChange={(v) => setDialog((d) => ({ ...d, open: v }))} classId={classId} target={dialog.target} student={dialog.student}
        events={events} rewards={rewards} badges={badges} medals={medals} contextLabel={ctxLabel} onDone={reload} />
    </div>
  );
}
